/**
 * Zustand des Grundkalenders: Auswahl, Apartmentfilter und die Zeilenfolge nach Wohnungsverbund.
 * Werte gesetzt werden im Monat je Apartment; der Grundkalender reicht die Auswahl dorthin.
 */
import { useMemo, useState } from 'react';
import type { AvailabilityDistributionCalendarResponse } from '@/shared/api';
import { orderByFamily } from './calendarFamilyOrder';
import { useRangeSelection } from './useRangeSelection';

export function useGrundkalender(
  data: AvailabilityDistributionCalendarResponse,
  wholeFlatOf: ReadonlyMap<string, string>,
) {
  const selection = useRangeSelection();
  const [filter, setFilter] = useState<Set<string> | null>(null);
  const order = useMemo(
    () =>
      orderByFamily(
        filter
          ? data.apartments.filter((apartment) => filter.has(apartment.propertyId))
          : data.apartments,
        wholeFlatOf,
      ),
    [filter, data.apartments, wholeFlatOf],
  );
  return {
    selection,
    filter,
    rows: order.rows,
    childIds: order.children,
    // Vorbelegt wird nur ein Apartment, das eindeutig gemeint ist: das gewählte oder das einzig gefilterte.
    focusedPropertyId:
      selection.propertyId ?? (filter?.size === 1 ? ([...filter][0] ?? null) : null),
    // Nur wenn der Filter die gewählte Zeile ausblendet, geht die Auswahl.
    changeFilter: (next: Set<string> | null) => {
      setFilter(next);
      if (next !== null && selection.propertyId !== null && !next.has(selection.propertyId)) {
        selection.clear();
      }
    },
  };
}
